import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { ConfirmationModal, Header, Loader, SideMenu, Success } from '../../components';
import { customStyles } from '../../Utils/utils';
import { AppDispatch, RootState } from '../../store/store';
import {
  NORMAL_PLAYBACK_RATE,
  resolvePlaybackRate,
} from '../../Utils/playbackPreference';
import { getImageUrlsApi } from '../../rtk-apis/express/getImageUrls';
import { getClipSettings } from '../../rtk-apis/express/getClipSettings';
import { saveClipSetting } from '../../rtk-apis/express/saveClipSetting';
import { clearState as clearImageUrlsState } from '../../store/slices/express/getImageUrls';
import { getReportFromPatientIdApi } from '../../rtk-apis/reports/getReportFromPatientId';
import { _expressUrls } from '../../constant';
import { getToken } from '../../Utils/authUtils';

import DetailsTile from './DetailsTile';
import './index.css';
import { ReportLayout } from '..';
import { extensionManager, servicesManager } from '../../../app/src/App';

import VideoPopUp from './VideoPopUp';
import ImageGallery from './components/ImageGallery';
import QuadView from './components/QuadView';
import CompareOverlay, { CompareInstance, PriorStudyOption } from './components/CompareOverlay';
import StudyOverview from './components/StudyOverview';
import ViewerControlsBar from './components/ViewerControlsBar';
import { stepVideoFrame, watchFrameDuration } from '../../Utils/videoFrameStep';
import SizeToScreen from './components/SizeToScreen';
import StressViewPanel, { ViewGroup } from './components/StressViewPanel';
import MeasurementHistoryHover from './components/MeasurementHistoryHover';
import { quadViewPairs } from './enums';

// Type definitions
interface MediaInstance {
  mediaUrl: string;
  view?: string;
  stage?: string;
  // Acquisition order as recorded by the cart. Preserved by the conversion pipeline and
  // the only ordering signal available on studies that carry no view labels.
  instanceNumber?: string | number;
  seriesNumber?: string | number;
}



interface SavedClipRate {
  sopInstanceUID: string;
  playbackRate: number;
}

const REST_STAGES = ['Rest', 'REST', 'Baseline', 'BASELINE'];
const PEAK_STAGES = ['Peak', 'PEAK', 'Impost', 'IMPOST'];

const VIEW_DISPLAY_MAP: Record<string, string> = {
  '4CH': 'AP4',
  '2CH': 'AP2',
  APLAX: 'AP3',
  PLAX: 'LAX',
  'SAX-PM': 'SAX',
  ASX: 'pSAX-ap1',
};
const VIEW_ORDER = ['4CH', '2CH', 'APLAX', 'PLAX', 'SAX-PM', 'ASX'];

// Same order as the Views panel, so the stepper and arrow keys walk the list top to bottom.
// Unknown views keep their acquisition order after the known ones.
function sortViewsLikePanel(views: string[]): string[] {
  const rank = (v: string) => {
    const i = VIEW_ORDER.indexOf(v);
    return i === -1 ? VIEW_ORDER.length : i;
  };
  return views
    .map((v, i) => ({ v, i }))
    .sort((a, b) => rank(a.v) - rank(b.v) || a.i - b.i)
    .map(x => x.v);
}

function groupInstancesByView(instances: MediaInstance[]): ViewGroup[] {
  const groups: Record<string, ViewGroup> = {};

  for (const inst of instances) {
    const v = inst.view;
    if (!v || v.trim() === '' || v.toUpperCase() === 'NA') {
      continue;
    }
    if (!groups[v]) {
      groups[v] = {
        viewId: v,
        displayName: VIEW_DISPLAY_MAP[v] || v,
        restInstances: [],
        peakInstances: [],
        hasRest: false,
        hasPeak: false,
      };
    }
    if (REST_STAGES.includes(inst.stage)) {
      groups[v].restInstances.push(inst);
      groups[v].hasRest = true;
    } else if (PEAK_STAGES.includes(inst.stage)) {
      groups[v].peakInstances.push(inst);
      groups[v].hasPeak = true;
    }
  }

  const ordered: ViewGroup[] = [];
  const seen = new Set<string>();
  for (const v of VIEW_ORDER) {
    if (groups[v] && (groups[v].hasRest || groups[v].hasPeak)) {
      ordered.push(groups[v]);
      seen.add(v);
    }
  }
  for (const g of Object.values(groups)) {
    if (!seen.has(g.viewId) && (g.hasRest || g.hasPeak)) {
      ordered.push(g);
    }
  }
  return ordered;
}

interface ModifiedUrls {
  instances?: MediaInstance[];
}

const ExpressMode = () => {
  const childRef = useRef();
  const containerRef = useRef(null);
  const [reportPanel, setReportPanel] = useState<HTMLElement | null>(null);
  const videoRefs = useRef([]);
  // The clip shown in the 1-up view (SizeToScreen).
  const singleVideoRef = useRef<HTMLVideoElement | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  // get data from params
  const params = useParams();
  const location = useLocation();
  const studyData = location.state;
  const studyInstanceUID = params?.studyInstanceUIDs;

  // Redux selectors
  const {
    status: imageUrlsStatus,
    result: imageUrlsResult,
    loading: imageUrlsLoading,
  } = useSelector(
    (state: RootState) =>
      state.getImageUrlsResponse || { status: null, result: null, loading: false }
  );
  const { loading: currentReportLoading } = useSelector(
    (state: RootState) => state.getCurrentReportResponse || { loading: false }
  );
  const [IsLoading, setIsLoading] = useState(false);
  // DICOM metadata is only needed by the report, so it loads behind the media
  // instead of blocking the page.
  const [isMetadataLoading, setIsMetadataLoading] = useState(false);
  // SR series loaded: enough to open an already-saved report.
  const [isReportViewReady, setIsReportViewReady] = useState(false);
  const loadingStudyRef = useRef<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedReportTemplate, setSelectedReportTemplate] = useState(null);
  const [openReportPanel, setOpenReportPanel] = useState(false);
  const breadcrumbs = [{ label: 'Express Mode', icon: 'sidebar-icon' }];

  // Loops autoplay on load, so the dock must start in the "playing" state or its
  // Pause button is out of sync with what's on screen.
  const [isPlaying, setIsPlaying] = useState(true);
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [modifiedUrls, setModifiedUrls] = useState<ModifiedUrls | null>(null);
  // Rates a reader saved by hand, SOPInstanceUID → rate. Outranks the user default.
  const [clipRates, setClipRates] = useState<Record<string, number>>({});
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isStudyMetaDataFetched, setIsStudyMetaDataFetched] = useState(false);
  const [loadingVideos, setLoadingVideos] = useState({});
  const [rigthWidth, setRigthWidth] = useState(0);
  const [rightHeight, setRightHeight] = useState(0);
  const [gridSize, setGridSize] = useState('30%');
  const [videoPopupIndex, setVideoPopupIndex] = useState(null);
  const [isSizeToScreen, setIsSizeToScreen] = useState(true);
  const [currIndex, setCurrIndex] = useState(0);
  const [selectedViewType, setSelectedViewType] = useState(0);
  const [selectedStageType, setSelectedStageType] = useState('NORMAL');
  const [restImages, setRestImages] = useState([]);
  const [peakImages, setPeakImages] = useState([]);
  const [viewList, setViewList] = useState([]);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const viewportWrapperRef = useRef();

  const [isCompareOpen, setIsCompareOpen] = useState(false);
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);
  // Compare Previous Studies: the patient's other studies + their loaded loops.
  const [priorStudies, setPriorStudies] = useState<PriorStudyOption[]>([]);
  const [priorInstances, setPriorInstances] = useState<CompareInstance[]>([]);
  const [loadingPriorUID, setLoadingPriorUID] = useState<string | null>(null);
  const [videoStressPopupIndex, setVideoStressPopupIndex] = useState<number | null>(null);
  const [stressMediaUrls, setStressMediaUrls] = useState<string[]>([]);
  const [viewGroups, setViewGroups] = useState<ViewGroup[]>([]);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [currQuadIdx, setCurrQuadIdx] = useState(0);

  const dataSource = extensionManager.getActiveDataSource()[0];

  // Helpers to resolve media URLs based on local PACS override
  const getPacsOverride = () => {
    try {
      let overrideStr: string | null = null;
      if (typeof document !== 'undefined' && document.cookie) {
        const m = document.cookie.match(/(?:^|; )pacs_override=([^;]*)/);
        if (m) {
          overrideStr = decodeURIComponent(m[1]);
        }
      }
      if (!overrideStr && typeof localStorage !== 'undefined') {
        overrideStr = localStorage.getItem('pacs_override');
      }
      if (!overrideStr && typeof sessionStorage !== 'undefined') {
        overrideStr = sessionStorage.getItem('pacs_override');
      }
      if (overrideStr) {
        return JSON.parse(overrideStr);
      }
    } catch (e) {}
    return null;
  };

  const resolveInstanceUrl = (inst: any, ov: any) => {
    const isLocal =
      ov && ov.ipAddress && ov.port && ov.ipAddress !== 'default' && ov.port !== '0000';
    const localURL = ov?.localURL || '';
    const localPath = inst?.localPath || '';
    if (isLocal && localURL && localPath) {
      const base = localURL.replace(/\/+$/, '');
      const path = localPath.replace(/^\/app/, '');
      return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
    }
    return inst?.mediaUrl || '';
  };

  const handleConfirmButtonClick = () => {
    if (childRef.current) {
      childRef.current.triggerChildFunction();
    }
    setShowConfirmationModal(false);
  };

  async function fetchAndStoreStudyMetadata(StudyInstanceUID, onSrReady?: () => void) {
    try {
      loadingStudyRef.current = StudyInstanceUID;
      setIsMetadataLoading(true);
      setIsReportViewReady(false);

      // The data source must be initialized before anything is retrieved from it.
      // initialize() is what builds the QIDO/WADO clients; the normal viewer gets
      // this from its route (DataSourceWrapper / Mode), but this page talks to the
      // data source directly and so has to do it itself.
      //
      // Skipping it threw "generateWadoHeader is not a function", the study
      // metadata never reached DicomMetadataStore, and every measurement on the
      // report rendered blank. It looked intermittent because reaching this page
      // via the study list initializes the data source as a side effect — only a
      // direct URL or a hard refresh landed here uninitialized.
      //
      // Safe to call on every fetch: it just rebuilds the config copies and
      // clients. No config in this deployment implements onConfiguration, so the
      // empty params/query carry no meaning to lose.
      if (typeof dataSource?.initialize === 'function') {
        await dataSource.initialize({ params: {}, query: new URLSearchParams() });
      }

      // Retrieve all series metadata for the given study instance UID
      const seriesMetadataPromises = await dataSource.retrieve.series.metadata({
        StudyInstanceUID,
        returnPromises: true,
      });
      // Opening or generating a report needs only the SR series; the image series
      // can take 5-10s on a cold PACS. The one report value read from the images,
      // the HeartRate tag, is filled in by ReportLayout once they have all arrived
      // (imageMetadataReady). The data source stores each series' instances itself
      // as it arrives.
      const started = seriesMetadataPromises.map(p => p.start());
      const srLoads = started.filter(
        (_, i) => seriesMetadataPromises[i]?.metadata?.Modality === 'SR'
      );
      Promise.allSettled(srLoads).then(() => {
        if (loadingStudyRef.current === StudyInstanceUID) {
          setIsReportViewReady(true);
          onSrReady?.();
        }
      });

      await Promise.all(started);

      setIsStudyMetaDataFetched(true); // Enable button after fetching is complete
    } catch (error) {
      console.error(
        `Failed to fetch or store metadata for StudyInstanceUID: ${StudyInstanceUID}`,
        error
      );
    } finally {
      if (loadingStudyRef.current === StudyInstanceUID) {
        setIsMetadataLoading(false);
        // Never leave View Report locked: on failure the SR may simply be missing.
        setIsReportViewReady(true);
        onSrReady?.();
      }
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      setOpenReportPanel(false);
      setSelectedReportTemplate(null);

      // Open the clinic's default template as soon as the SR is in, not after
      // every image series. Once only: the failure path calls this too.
      let opened = false;
      await fetchAndStoreStudyMetadata(studyInstanceUID, () => {
        if (opened || !studyData?.defaultTemp) return;
        opened = true;
        setSelectedReportTemplate(studyData?.defaultTemp);
        setOpenReportPanel(true);
      });
    };

    fetchData();
  }, [studyInstanceUID]);

  useEffect(() => {
    if (studyInstanceUID) {
      dispatch(getImageUrlsApi({ studyUID: studyInstanceUID }));
    }
  }, [studyInstanceUID, dispatch]);

  // Handle API response
  useEffect(() => {
    if (imageUrlsStatus === 'fulfilled' && imageUrlsResult) {
      const data = imageUrlsResult;
      const ov = getPacsOverride();
      const instances = data?.data?.[0]?.instances || [];
      const resolvedUrls = instances.map(obj => resolveInstanceUrl(obj, ov));

      setMediaUrls(resolvedUrls);
      setModifiedUrls(data?.data?.[0]);

      if (studyData?.isStressMode) {
        setSelectedStageType('STRESS');
        setSelectedViewType(0);
      }

      const loadingState = (resolvedUrls || []).reduce((acc, _, index) => {
        acc[index] = true;
        return acc;
      }, {});
      setLoadingVideos(loadingState);
    } else if (imageUrlsStatus === 'rejected') {
      console.error('[Peak] Error fetching media:', imageUrlsResult);
      setMediaUrls([]);
      setModifiedUrls(null);
      setLoadingVideos({});
    } else if (imageUrlsStatus === 'pending') {
      setMediaUrls([]);
      setModifiedUrls(null);
      setLoadingVideos({});
    }
    return () => {
      dispatch(clearImageUrlsState());
    };
  }, [imageUrlsStatus, imageUrlsResult, studyData?.isStressMode]);

  const handleVideoLoaded = index => {
    setLoadingVideos(prev => ({
      ...prev,
      [index]: false,
    }));
    const videoEl = videoRefs.current[index];
    const url = videoEl?.querySelector('source')?.getAttribute('src') || mediaUrls[index];
    if (videoEl && url) {
      videoEl.playbackRate = resolveRate(url);
    }
  };

  // Extract SOPInstanceUID from converted media filename.
  // Convention: {SOP}_{stage}_{view}_{instance#}_{series#}_{AET}_{StudyDate}.webm
  function getSopInstanceUIDFromUrl(url: string): string {
    if (!url) {
      return '';
    }
    try {
      const parsed = new URL(url, window.location.origin);
      const filename = parsed.pathname.split('/').pop() || '';
      const stem = filename.replace(/__.*$/, '').replace(/\.[^.]+$/, '');
      return stem.split('_')[0] || '';
    } catch {
      return '';
    }
  }

  /**
   * The speed a clip should play at: a rate saved by a reader wins, then the
   * reader's profile default, otherwise 1x. Doppler detection now comes from the
   * pipeline, so no clip is flagged Doppler here.
   *
   * Memoized because every consumer re-applies the rate in an effect keyed on this
   * identity — the saved rates arrive from the network
   * AFTER the first video has already fired `onLoadedData`, so applying the rate
   * only at load time left the first clip stuck at 1x (the original bug).
   */
  /**
   * This reader's own default speed, from their profile. Undefined until the profile
   * lands, which is why `resolvePlaybackRate` treats anything unrecognised as "no
   * preference" rather than guessing.
   */
  // Cast because the profile slice types its payload as `string`; the resolver validates
  // the value anyway, so anything unexpected reads as "no preference".
  const userPlaybackRate = useSelector(
    (state: RootState) =>
      (state.getProfileDetailsResponse?.data as unknown as { defaultPlaybackRate?: unknown })
        ?.defaultPlaybackRate
  );

  const resolveRate = useCallback(
    (url: string): number => {
      const sop = getSopInstanceUIDFromUrl(url);
      if (!sop) {
        return NORMAL_PLAYBACK_RATE;
      }
      return resolvePlaybackRate(clipRates[sop], userPlaybackRate, false);
    },
    [clipRates, userPlaybackRate]
  );

  // Hydrate saved per-clip rates for this study.
  useEffect(() => {
    if (!studyInstanceUID) {
      return;
    }
    (async () => {
      try {
        const res = await dispatch(getClipSettings({ studyUID: studyInstanceUID }));
        const rows = (res?.payload as { data?: SavedClipRate[] })?.data;
        if (!Array.isArray(rows)) {
          return;
        }
        const next: Record<string, number> = {};
        rows.forEach(r => {
          if (r?.sopInstanceUID && typeof r.playbackRate === 'number') {
            next[r.sopInstanceUID] = r.playbackRate;
          }
        });
        setClipRates(next);
      } catch {
        /* no saved rates — fall back to the default speed */
      }
    })();
  }, [studyInstanceUID, dispatch]);

  // The clip the speed control acts on. Scoped to the normal echo view — the stress
  // panels track their own index, so `currIndex` would point at the wrong loop there.
  const activeClipUrl = selectedStageType === 'NORMAL' ? mediaUrls[currIndex] : '';

  /** Change the active clip's speed and persist it for everyone who opens it next. */
  const handleRateChange = (rate: number) => {
    const url = activeClipUrl;
    const sop = url ? getSopInstanceUIDFromUrl(url) : '';
    if (!sop || !studyInstanceUID) {
      return;
    }
    setClipRates(prev => ({ ...prev, [sop]: rate }));
    dispatch(
      saveClipSetting({ studyUID: studyInstanceUID, sopInstanceUID: sop, playbackRate: rate })
    );
  };

  // Re-apply the rate to already-rendered grid videos whenever a saved rate lands
  // after the video loaded. Only reaches the grid — SizeToScreen,
  // VideoPopUp and CompareOverlay own their own videos and re-apply it themselves.
  useEffect(() => {
    videoRefs.current.forEach((video, idx) => {
      if (!video) {
        return;
      }
      const url = video.querySelector?.('source')?.getAttribute('src') || mediaUrls[idx];
      if (url) {
        video.playbackRate = resolveRate(url);
      }
    });
  }, [resolveRate, mediaUrls]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    const body = document.getElementsByTagName('body')[0];
    body.classList.toggle('sidebar-hidden', !isSidebarOpen);
  };

  const togglePlayAll = () => {
    setIsPlaying(prev => !prev);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const video = entry.target as HTMLVideoElement;
          if (isPlaying && entry.isIntersecting) {
            video.play().catch(error => console.error('Error playing video:', error));
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 } // At least 50% of the video must be visible
    );

    videoRefs.current.forEach(video => {
      if (video) {
        observer.observe(video);
      }
    });

    return () => observer.disconnect();
    // Grid videos remount when leaving 1-up, switching stage or loading media, so
    // re-observe the new elements then too.
  }, [isPlaying, isSizeToScreen, selectedStageType, mediaUrls]);

  const handleTileDoubleClick = index => {
    setVideoPopupIndex(index);
  };

  // Instances with resolved URLs + view/stage metadata for the compare overlay.
  const compareInstances: CompareInstance[] = useMemo(() => {
    const instances = modifiedUrls?.instances || [];
    const ov = getPacsOverride();
    return instances
      .map(inst => ({
        url: resolveInstanceUrl(inst, ov),
        view: inst.view,
        stage: inst.stage,
        instanceNumber: inst.instanceNumber,
        seriesNumber: inst.seriesNumber,
      }))
      .filter(inst => !!inst.url);
  }, [modifiedUrls]);

  // DICOM "YYYYMMDD" → "DD/MM/YYYY" for the prior-study picker labels.
  const formatShortDate = (raw?: string) => {
    const s = String(raw || '');
    const m = s.match(/^(\d{4})(\d{2})(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : s || 'Study';
  };

  // Compare Previous Studies: on first open, discover the patient's OTHER studies
  // (by MRN) so they can be pulled into the compare grid. Failure is silent — the
  // picker just stays empty and single-study compare works exactly as before.
  useEffect(() => {
    const patientId = studyData?.mrn;
    if (!isCompareOpen || !patientId || priorStudies.length) {
      return;
    }
    (async () => {
      const seen = new Set<string>();
      const opts: PriorStudyOption[] = [];
      const add = (uid?: string, label?: string) => {
        if (!uid || uid === studyInstanceUID || seen.has(uid)) {
          return;
        }
        seen.add(uid);
        opts.push({ studyInstanceUID: uid, label: label || 'Study', loaded: false });
      };

      // Primary: the patient's DICOM study history (QIDO by PatientID) — this is
      // where prior echoes live; a patient may have many studies but few reports.
      try {
        if (dataSource?.query?.studies?.search) {
          const studies: any[] = await dataSource.query.studies.search({ patientId });
          (studies || []).forEach((s: any) => {
            const uid = s?.studyInstanceUid || s?.studyInstanceUID;
            const desc = s?.description || (Array.isArray(s?.modalities) ? s.modalities.join('/') : s?.modalities);
            add(uid, `${formatShortDate(s?.date)}${desc ? ` · ${desc}` : ''}`);
          });
        }
      } catch {
        /* QIDO unavailable — fall through to the report DB */
      }

      // Supplement: reported studies (covers studies present in the report DB).
      try {
        const res: any = await dispatch(getReportFromPatientIdApi({ patientId })).unwrap();
        (res?.result || []).forEach((r: any) =>
          add(r?.studyInstanceUID, `${formatShortDate(r?.studyDate)}${r?.reportType ? ` · ${r.reportType}` : ''}`)
        );
      } catch {
        /* silent — prior-study compare is additive */
      }

      if (opts.length) {
        setPriorStudies(opts);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompareOpen, studyData?.mrn, studyInstanceUID]);

  // Pull a prior study's loops into the compare pool on demand. Uses the SAME
  // lite-image endpoint as the current study (POST /liteimg/getLiteDcmImgs →
  // data.data[0].instances) via a direct fetch, so it does NOT go through the
  // shared getImageUrls slice (which would clobber the current study's viewer).
  const handleLoadPriorStudy = async (uid: string) => {
    setLoadingPriorUID(uid);
    try {
      const token = getToken();
      const resp = await fetch(_expressUrls.GET_IMAGE_URLS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ studyUID: uid }),
      });
      const data: any = resp.ok ? await resp.json() : null;
      const insts = data?.data?.[0]?.instances || [];
      const ov = getPacsOverride();
      const label = priorStudies.find(p => p.studyInstanceUID === uid)?.label || 'Prior study';
      const mapped: CompareInstance[] = insts
        .map((inst: any) => ({
          url: resolveInstanceUrl(inst, ov),
          view: inst.view,
          stage: inst.stage,
          studyInstanceUID: uid,
          studyLabel: label,
          isCurrent: false,
        }))
        .filter((i: CompareInstance) => !!i.url);
      if (mapped.length) {
        setPriorInstances(prev => [...prev.filter(p => p.studyInstanceUID !== uid), ...mapped]);
        setPriorStudies(prev =>
          prev.map(p => (p.studyInstanceUID === uid ? { ...p, loaded: true } : p))
        );
      }
      // If 0 loops came back, leave the button active so it can be retried.
    } catch {
      /* silent — study may have no lite images */
    } finally {
      setLoadingPriorUID(null);
    }
  };

  // Current study's loops (tagged) + any pulled-in prior studies' loops.
  const mergedCompareInstances: CompareInstance[] = useMemo(() => {
    const current = compareInstances.map(i => ({
      ...i,
      isCurrent: true,
      studyLabel: i.studyLabel || 'Current study',
    }));
    return [...current, ...priorInstances];
  }, [compareInstances, priorInstances]);

  const handleVideoClick = (videoUrl: string, videoList: string[], index: number) => {
    setStressMediaUrls(videoList);
    setVideoStressPopupIndex(index);
  };

  const handleNavClick = command => {
    if (command === 'next' && currIndex < mediaUrls?.length - 1) {
      setCurrIndex(prev => prev + 1);
    } else if (command === 'prev' && currIndex > 0) {
      setCurrIndex(prev => prev - 1);
    }
  };

  useEffect(() => {
    const handleKeyDown = event => {
      // Overlays own the arrow keys while they are open, so the page must not also
      // step the underlying single-image viewer behind them.
      if (isSizeToScreen && !isCompareOpen && !isOverviewOpen) {
        if (event.key === 'ArrowRight') {
          handleNavClick('next');
        } else if (event.key === 'ArrowLeft') {
          handleNavClick('prev');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [mediaUrls, isSizeToScreen, currIndex, isCompareOpen, isOverviewOpen]);

  // Space plays/pauses every loop on the page; Up/Down step paused loops one frame.
  // Both work in 1-up and grid views.
  useEffect(() => {
    const handleViewerKeys = (event: KeyboardEvent) => {
      const isSpace = event.code === 'Space';
      const isStep = event.key === 'ArrowUp' || event.key === 'ArrowDown';
      if (!isSpace && !isStep) {
        return;
      }
      if (isSpace && event.repeat) {
        return;
      }
      // Typing in the report or a form keeps its space.
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) {
        return;
      }
      // Overlays and popups run their own players.
      if (
        isCompareOpen ||
        isOverviewOpen ||
        videoPopupIndex !== null ||
        videoStressPopupIndex !== null
      ) {
        return;
      }
      if (isStep) {
        if ((selectedStageType || 'NORMAL') !== 'NORMAL') {
          return;
        }
        const dir = event.key === 'ArrowDown' ? 1 : -1;
        const videos = isSizeToScreen ? [singleVideoRef.current] : videoRefs.current;
        const stepped = videos.filter(v => v?.isConnected).map(v => stepVideoFrame(v, dir));
        if (stepped.some(Boolean)) {
          // Stops the page scrolling and the native player changing volume.
          event.preventDefault();
        }
        return;
      }
      // Stops the page scrolling and a focused button from also firing.
      event.preventDefault();
      setIsPlaying(prev => !prev);
    };

    window.addEventListener('keydown', handleViewerKeys);
    return () => {
      window.removeEventListener('keydown', handleViewerKeys);
    };
  }, [
    isCompareOpen,
    isOverviewOpen,
    videoPopupIndex,
    videoStressPopupIndex,
    isSizeToScreen,
    selectedStageType,
  ]);

  useEffect(() => {
    if (selectedStageType === 'STRESS') {
      const instances = modifiedUrls?.instances || [];

      // Extract and filter out empty strings and 'NA' (case-insensitive)
      const viewNames = instances
        ?.map(y => y.view)
        .filter(name => name?.trim() !== '' && name?.toUpperCase() !== 'NA');
      const stageNames = instances
        ?.map(y => y.stage)
        .filter(name => name?.trim() !== '' && name?.toUpperCase() !== 'NA');

      const uniqueNames = new Set([...viewNames, ...stageNames]);

      // Separate unique values into viewName and stageName without overlap
      const viewName = sortViewsLikePanel(viewNames?.filter(name => uniqueNames.delete(name)) || []);
      const stageName = stageNames?.filter(name => uniqueNames.delete(name));

      setViewList(viewName);
      const filteredData = instances?.filter(x => x.view === viewName[selectedViewType || 0]);

      setRestImages(filteredData.filter(x => REST_STAGES.includes(x.stage)));
      setPeakImages(filteredData?.filter(x => PEAK_STAGES.includes(x.stage)) ?? []);
    } else if (selectedStageType === 'QUAD') {
      const instances = modifiedUrls?.instances || [];
      setRestImages(instances.filter(x => REST_STAGES.includes(x.stage)));
      setPeakImages(instances.filter(x => PEAK_STAGES.includes(x.stage)));
    } else if (
      selectedStageType === 'NORMAL' &&
      modifiedUrls?.instances &&
      modifiedUrls.instances.length > 0
    ) {
      const ov = getPacsOverride();
      const filteredData = modifiedUrls.instances.map(x => resolveInstanceUrl(x, ov));
      setRestImages([]);
      setPeakImages([]);
      setMediaUrls(filteredData);
    }
  }, [selectedStageType, selectedViewType]);

  useEffect(() => {
    const instances = modifiedUrls?.instances || [];
    setViewGroups(groupInstancesByView(instances));
  }, [modifiedUrls]);

  useEffect(() => {
    const reloaded = sessionStorage.getItem('reloaded');

    if (!reloaded) {
      sessionStorage.setItem('reloaded', 'true');
    } else {
      sessionStorage.removeItem('reloaded');
      navigate('/');
    }

    return () => {
      sessionStorage.removeItem('reloaded'); // Reset when navigating away
    };
  }, [navigate]);

  const handleRefreshImageUrls = () => {
    console.log('Refreshing image URLs for study:', studyInstanceUID);
    if (studyInstanceUID) {
      // Force re-render by clearing state first
      setMediaUrls([]);
      setModifiedUrls(null);
      setLoadingVideos({});

      // Clear Redux state and make new API call
      dispatch(clearImageUrlsState());
      dispatch(getImageUrlsApi({ studyUID: studyInstanceUID }));
    }
  };

  const handleStressViewSelect = (viewId: string) => {
    const idx = viewList.indexOf(viewId);
    if (idx !== -1) {
      setSelectedViewType(idx);
    }
  };

  // Clamped like the stepper's disabled ends, so holding an arrow stops at the last view.
  const handleStressViewStep = (dir: 1 | -1) => {
    setSelectedViewType(prev => {
      const next = (Number(prev) || 0) + dir;
      return next < 0 || next >= viewList.length ? prev : next;
    });
  };

  const handleQuadViewSelect = (viewId: string) => {
    const pageIdx = quadViewPairs.findIndex(pair => pair.includes(viewId));
    if (pageIdx !== -1) {
      setCurrQuadIdx(pageIdx);
    }
  };

  const handleFullScreen = () => {
    if (viewportWrapperRef.current) {
      const requestFullscreen =
        viewportWrapperRef.current.requestFullscreen ||
        viewportWrapperRef.current.webkitRequestFullscreen ||
        viewportWrapperRef.current.msRequestFullscreen ||
        viewportWrapperRef.current.mozRequestFullScreen;

      if (requestFullscreen) {
        requestFullscreen
          .call(viewportWrapperRef.current)
          .then(() => {
            setIsFullScreen(true);
            console.log('Entered fullscreen mode successfully');
          })
          .catch(err => {
            console.error('Failed to enter fullscreen mode:', err);
          });
      } else {
        console.error('Fullscreen API is not supported in this browser.');
      }
    } else {
      console.error('No element to enter fullscreen mode.');
    }
  };

  const exitFullScreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    }

    // Restore scrolling when exiting fullscreen
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      handleFullScreen();
    } else {
      exitFullScreen();
    }
  };

  return (
    <>
      <section
        className="layoutContainer"
        style={{ ...customStyles.layoutBackground, overflow: 'hidden' }}
      >
        <div className="layoutGrid">
          <div className="page">
            <Header
              breadcrumbs={breadcrumbs}
              onSidebarToggle={toggleSidebar}
              isSidebarOpen={isSidebarOpen}
            />
            <div
              className="page__reportBody"
              style={{ borderTop: '1px solid #fff' }}
            >
              <DetailsTile
                studyData={studyData}
                setSelectedReportTemplate={setSelectedReportTemplate}
                setOpenReportPanel={setOpenReportPanel}
                isStudyMetaDataFetched={isStudyMetaDataFetched}
                isLoading={IsLoading || !isReportViewReady}
                isViewLoading={IsLoading || !isReportViewReady}
                toggleFullScreen={toggleFullScreen}
                onBeforeRegenerate={async () => {
                  // Land any pending typing before the rebuild replaces it, so a late
                  // save can't overwrite the regenerated body.
                  try {
                    await (childRef.current as any)?.flushPendingChanges?.();
                  } catch {
                    // the rebuild replaces it anyway
                  }
                  setOpenReportPanel(false);
                }}
              />
              {videoPopupIndex !== null && (
                <VideoPopUp
                  url={mediaUrls}
                  setVideoPopupIndex={setVideoPopupIndex}
                  videoPopupIndex={videoPopupIndex}
                  getPlaybackRate={resolveRate}
                />
              )}
              {videoStressPopupIndex !== null && (
                <VideoPopUp
                  url={stressMediaUrls}
                  setVideoPopupIndex={setVideoStressPopupIndex}
                  videoPopupIndex={videoStressPopupIndex}
                  getPlaybackRate={resolveRate}
                />
              )}
              <div
                ref={viewportWrapperRef}
                style={isFullScreen ? { overflow: 'auto' } : {}}
                className={`express__layout ${openReportPanel ? 'expressLayoutReportOpen' : ''} mt-1 px-3 pb-3 pt-1`}
              >
                <div className="express__viewer_zone">
                  <ViewerControlsBar
                    selectedStageType={selectedStageType || 'NORMAL'}
                    onStageTypeChange={stage => {
                      setSelectedStageType(stage);
                      setSelectedViewType('');
                    }}
                    onViewTypeChange={setSelectedViewType}
                    selectedViewType={selectedViewType}
                    viewList={viewList.map(v => VIEW_DISPLAY_MAP[v] || v)}
                    isPlaying={isPlaying}
                    togglePlayAll={togglePlayAll}
                    isSizeToScreen={isSizeToScreen}
                    setIsSizeToScreen={setIsSizeToScreen}
                    playbackRate={activeClipUrl ? resolveRate(activeClipUrl) : 1}
                    onPlaybackRateChange={activeClipUrl ? handleRateChange : undefined}
                    setGridSize={setGridSize}
                    currentGridSize={gridSize}
                    onCompare={() => setIsCompareOpen(true)}
                    onOverview={() => setIsOverviewOpen(true)}
                    onRefreshImages={handleRefreshImageUrls}
                  />
                  {selectedStageType === 'STRESS' ? (
                    <div style={{ display: 'flex', width: '100%', alignItems: 'flex-start' }}>
                      <StressViewPanel
                        viewGroups={viewGroups}
                        selectedViewId={viewList[selectedViewType] || ''}
                        onViewSelect={handleStressViewSelect}
                        collapsed={isPanelCollapsed}
                        onToggleCollapse={() => setIsPanelCollapsed(p => !p)}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <ImageGallery
                          leftImages={restImages}
                          rightImages={peakImages}
                          onVideoClick={handleVideoClick}
                          isPlaying={isPlaying}
                          openReportPanel={openReportPanel}
                          onStepView={handleStressViewStep}
                          keyboardEnabled={
                            videoStressPopupIndex === null && !isCompareOpen && !isOverviewOpen
                          }
                        />
                      </div>
                    </div>
                  ) : null}
                  {selectedStageType === 'QUAD' ? (
                    <div style={{ display: 'flex', width: '100%', alignItems: 'flex-start' }}>
                      <StressViewPanel
                        viewGroups={viewGroups}
                        selectedViewId={(quadViewPairs[currQuadIdx] || [])[0] || ''}
                        onViewSelect={handleQuadViewSelect}
                        collapsed={isPanelCollapsed}
                        onToggleCollapse={() => setIsPanelCollapsed(p => !p)}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <QuadView
                          RestImages={restImages}
                          PeakImages={peakImages}
                          currQuadIdx={currQuadIdx}
                          onCurrQuadIdxChange={setCurrQuadIdx}
                        />
                      </div>
                    </div>
                  ) : null}
                  {selectedStageType === 'NORMAL' && (
                    <div className="express__image_container">
                      {mediaUrls.length === 0 ? (
                        <p className="w-full text-center">No media found</p>
                      ) : isSizeToScreen ? (
                        <SizeToScreen
                          mediaUrls={mediaUrls}
                          currIndex={currIndex}
                          isPlaying={isPlaying}
                          handleTileDoubleClick={handleTileDoubleClick}
                          handleNavClick={handleNavClick}
                          setCurrIndex={setCurrIndex}
                          getPlaybackRate={resolveRate}
                          videoRef={singleVideoRef}
                        />
                      ) : (
                        mediaUrls.map((url, index) => (
                          <div
                            key={index}
                            style={{ width: `${gridSize}` }}
                            className="flex items-center justify-center"
                          >
                            <div
                              className="relative w-full"
                              onDoubleClick={() => handleTileDoubleClick(index)}
                            >
                              {loadingVideos[index] && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-200 bg-opacity-75"></div>
                              )}
                              {url.endsWith('.webm') ? (
                                <video
                                  ref={el => {
                                    videoRefs.current[index] = el;
                                    watchFrameDuration(el);
                                  }}
                                  controls
                                  width="100%"
                                  className="rounded-lg shadow-md"
                                  loop
                                  key={url}
                                  onLoadedData={() => handleVideoLoaded(index)}
                                  disablePictureInPicture
                                  controlsList="nofullscreen"
                                >
                                  <source
                                    src={url}
                                    type="video/webm"
                                  />
                                  Your browser does not support video.
                                </video>
                              ) : (
                                <img
                                  src={url}
                                  alt={`Media ${index}`}
                                  className="w-full rounded-lg shadow-md"
                                />
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {/* The vertical "Report" reopen button is gone (16 Jul): reopening from
                    the in-memory template raced the unmount autosave flush and restored
                    a stale body over live edits. Minimize still closes the panel; the
                    header "View Report" button is the single way back in — it always
                    refetches the saved report before mounting this panel. */}
                {openReportPanel && (
                  <div className="express__report_layout" ref={setReportPanel}>
                    <div className="express__report_minimize_bar">
                      <button
                        className="express__report_minimize_btn"
                        onClick={async () => {
                          // Persist any sub-debounce typing BEFORE the panel
                          // unmounts, and sync the flushed body into the
                          // in-memory template so nothing stale can ever be
                          // shown or re-saved after this point.
                          try {
                            const flushed = await (childRef.current as any)?.flushPendingChanges?.();
                            if (flushed) {
                              setSelectedReportTemplate((prev: any) =>
                                prev ? { ...prev, item: { ...prev?.item, templateHTML: flushed } } : prev
                              );
                            }
                          } catch {
                            // never block closing the panel on a failed flush
                          }
                          setOpenReportPanel(false);
                        }}
                        title="Minimise report"
                        aria-label="Minimise report"
                      >
                        {/* universal minimize glyph */}
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        >
                          <path d="M4 11h8" />
                        </svg>
                      </button>
                    </div>
                    <ReportLayout
                      key={selectedReportTemplate?.regenerateNonce ?? 'report'}
                      closeReportPanel={setOpenReportPanel}
                      reportTemplate={selectedReportTemplate}
                      ref={childRef}
                      setIsLoading={setIsLoading}
                      setIsSuccess={setIsSuccess}
                      handleButtonClick={() => setShowConfirmationModal(true)}
                      setRigthWidth={setRigthWidth}
                      setRightHeight={setRightHeight}
                      containerRef={containerRef}
                      imageMetadataReady={!isMetadataLoading}
                    />
                    <MeasurementHistoryHover
                      panel={reportPanel}
                      patientId={studyData?.mrn}
                      sex={studyData?.sex}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <StudyOverview
        open={isOverviewOpen}
        onClose={() => setIsOverviewOpen(false)}
        instances={compareInstances}
        getPlaybackRate={resolveRate}
      />
      <CompareOverlay
        open={isCompareOpen}
        onClose={() => setIsCompareOpen(false)}
        instances={mergedCompareInstances}
        initialUrl={mediaUrls[currIndex]}
        priorStudies={priorStudies}
        onLoadPriorStudy={handleLoadPriorStudy}
        loadingPriorUID={loadingPriorUID}
        getPlaybackRate={resolveRate}
      />
      <Loader isLoading={IsLoading || imageUrlsLoading || currentReportLoading} />
      <Success
        isSuccessModalOpen={isSuccess}
        title="Report drafted successfully."
        handleCloseModal={() => {
          setIsSuccess(false);
          navigate('/reports');
        }}
      />
      <ConfirmationModal
        title="Report Status!"
        description="Are you sure you want to save the report?"
        isConfirmationModalOpen={showConfirmationModal}
        handleCloseModal={() => setShowConfirmationModal(false)}
        handleConfirm={handleConfirmButtonClick}
        cancelActionText="No, Cancel"
        confirmActionText="Yes, Confirm"
        isPatientReport={true}
      />
    </>
  );
};

export default ExpressMode;
